import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchOrders, updateOrderStatus, updateItemStatus, getRole, hasFeature, openOrdersWebSocket, fetchMyAssignments, rescheduleOrder } from "../services/api";
import OrderCard from "../components/OrderCard";
import { parseServerDate } from "../utils/time";
import { useT } from "../i18n/useT";

// Stable keys for the tab strip; labels resolved via t() at render time
// so the navbar language switcher affects the kitchen view live.
const STATUS_TAB_KEYS = ["active", "scheduled", "ready_run", "pending", "preparing", "ready", "served", "all"];

// Station code → friendly label for the "Ready to run" list, so a waiter
// can see which station each ready item is waiting at.
const STATION_LABELS = { kitchen: "🍳 Kitchen", bar: "🍺 Bar", cafe: "☕ Café" };
const stationLabel = (s) => STATION_LABELS[s] || "🍽️ Kitchen";

const TERMINAL = new Set(["served", "completed", "cancelled"]);

export default function KitchenPage() {
  const [orders, setOrders] = useState([]);
  const [station, setStation] = useState(null);
  const stationsOn = hasFeature("stations");
  const [tab, setTab] = useState("active");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false); // websocket connected
  // Waiter's own table assignments for today (used by the "Your tables"
  // strip at the top of the page). Empty array for chefs + managers
  // since fetchMyAssignments returns [] when there's no staff_id on the
  // token (i.e. owner login).
  const [myTables, setMyTables] = useState([]);
  const role = getRole();
  const { t, lang } = useT();

  // Fetch "my tables today" once on mount when the user is a waiter.
  // Refresh every 5 minutes in case the manager reassigns mid-shift.
  useEffect(() => {
    if (role !== "waiter") return;
    let cancelled = false;
    const load = () => {
      fetchMyAssignments()
        .then((data) => { if (!cancelled) setMyTables(data || []); })
        .catch(() => { /* silent — the widget just stays hidden / empty */ });
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [role]);


  // Map status-tab keys to translated labels. "active" and "all" are
  // dashboard-specific tabs (not real statuses); the other four mirror
  // the order_status values used throughout the system.
  const tabLabel = (key) => {
    switch (key) {
      case "active":    return t("tab_active");
      case "scheduled": return t("tab_scheduled");
      case "ready_run": return t("tab_ready_to_run");
      case "pending":   return t("status_pending");
      case "preparing": return t("status_preparing");
      case "ready":     return t("status_ready");
      case "served":    return t("status_served");
      case "all":       return t("tab_all_today");
      default:          return key;
    }
  };

  // Polling timer (only used as fallback when WS is disconnected)
  const fallbackTimer = useRef(null);

  // Stable refs so the WS effect doesn't have to depend on tab
  const tabRef = useRef(tab);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  const stationRef = useRef(station);
  useEffect(() => { stationRef.current = station; }, [station]);
  useEffect(() => { load(); }, [station]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    try {
      const data = await fetchOrders({ activeOnly: tabRef.current === "active" || tabRef.current === "ready_run", todayOnly: true, station: tabRef.current === "ready_run" ? null : stationRef.current });
      setOrders(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load on mount + when tab changes
  useEffect(() => {
    setLoading(true);
    load();
  }, [tab, load]);

  // WebSocket connection with auto-reconnect + polling fallback
  useEffect(() => {
    let ws = null;
    let closed = false;
    let reconnectTimer = null;
    let backoff = 1000; // grows on consecutive failures

    const upsertFromEvent = (incoming) => {
      const isActiveTab = tabRef.current === "active" || tabRef.current === "ready_run";
      setOrders((cur) => {
        const idx = cur.findIndex((o) => o.id === incoming.id);
        // If we're on the "active" tab and this order is now terminal, drop it.
        if (isActiveTab && TERMINAL.has(incoming.status)) {
          if (idx >= 0) {
            const next = [...cur];
            next.splice(idx, 1);
            return next;
          }
          return cur;
        }
        // Filtered tabs (e.g. "preparing"): keep only matching state, drop others.
        if (tabRef.current !== "active" && tabRef.current !== "all" && tabRef.current !== "ready_run" && incoming.status !== tabRef.current) {
          if (idx >= 0) {
            const next = [...cur];
            next.splice(idx, 1);
            return next;
          }
          return cur;
        }
        if (idx >= 0) {
          const next = [...cur];
          next[idx] = incoming;
          return next;
        }
        return [...cur, incoming];
      });
    };

    const stopFallback = () => {
      if (fallbackTimer.current) {
        clearInterval(fallbackTimer.current);
        fallbackTimer.current = null;
      }
    };
    const startFallback = () => {
      stopFallback();
      // 8s polling while disconnected — slower than the old 5s since
      // disconnects should be rare; saves API hits.
      fallbackTimer.current = setInterval(load, 8000);
    };

    const connect = async () => {
      if (closed) return;
      // Minting a connect ticket is async now; guard against unmount/close and
      // a stale socket if this effect tore down while we were awaiting.
      const sock = await openOrdersWebSocket();
      if (closed) { if (sock) { try { sock.close(); } catch (e) { /* noop */ } } return; }
      if (!sock) {           // no token / ticket → poll instead of pushing
        startFallback();
        if (!closed) { reconnectTimer = setTimeout(connect, backoff); backoff = Math.min(backoff * 2, 30000); }
        return;
      }
      ws = sock;

      ws.onopen = () => {
        setLive(true);
        backoff = 1000;
        stopFallback();
      };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "order.created" || msg.type === "order.updated") {
          upsertFromEvent(msg.order);
        }
        // "ping" / "hello" → ignore
      };
      ws.onclose = () => {
        setLive(false);
        startFallback();
        if (closed) return;
        reconnectTimer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30000);
      };
      ws.onerror = () => {
        // onclose will fire right after, handle there
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      stopFallback();
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
      }
    };
  }, [load]);

  const handleStatus = async (orderId, newStatus) => {
    // Cancelling an order now requires a reason (loss prevention / audit).
    let reason;
    if (newStatus === "cancelled") {
      const L = { en: "Reason for cancelling this order:", am: "ትዕዛዙን ለመሰረዝ ምክንያት:", om: "Ajaja kana haquuf sababa:" };
      reason = window.prompt(L[lang] || L.en);
      if (!reason || !reason.trim()) return;
      reason = reason.trim();
    }
    try {
      // The PATCH returns the updated order. Apply it immediately so the
      // UI feels instant, even if the WebSocket push is delayed or the
      // socket has dropped. The same upsert logic the WS uses keeps tabs
      // consistent (drops terminal orders from the active tab, etc).
      const updated = await updateOrderStatus(orderId, newStatus, reason);
      const isActiveTab = tabRef.current === "active" || tabRef.current === "ready_run";
      setOrders((cur) => {
        const idx = cur.findIndex((o) => o.id === updated.id);
        if (isActiveTab && TERMINAL.has(updated.status)) {
          if (idx >= 0) {
            const next = [...cur];
            next.splice(idx, 1);
            return next;
          }
          return cur;
        }
        if (tabRef.current !== "active" && tabRef.current !== "all" && tabRef.current !== "ready_run" && updated.status !== tabRef.current) {
          if (idx >= 0) {
            const next = [...cur];
            next.splice(idx, 1);
            return next;
          }
          return cur;
        }
        if (idx >= 0) {
          const next = [...cur];
          next[idx] = updated;
          return next;
        }
        return [...cur, updated];
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : detail?.message || "Could not update status.";
      setError(msg);
    }
  };

  const handleItemStatus = async (orderId, itemId, newStatus) => {
    try {
      const updated = await updateItemStatus(orderId, itemId, newStatus);
      setOrders((cur) => cur.map((o) => (o.id === updated.id ? updated : o)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update item.");
    }
  };

  const fireNow = async (orderId) => {
    try {
      const updated = await rescheduleOrder(orderId, null);   // null = ASAP -> fires
      setOrders((cur) => cur.map((o) => (o.id === updated.id ? updated : o)));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not fire the order.");
    }
  };

  const serveAtLabel = (o) => {
    const ms = parseServerDate(o.requested_at);
    if (!ms) return "";
    try { return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  const visible = useMemo(() => {
    let list;
    if (tab === "scheduled") {
      // The Scheduled lane: timed orders still waiting to fire.
      list = orders.filter((o) => o.scheduled);
    } else if (tab === "active" || tab === "all") {
      // Live queues hide not-yet-fired scheduled orders.
      list = orders.filter((o) => !o.scheduled);
    } else {
      list = orders.filter((o) => o.status === tab && !o.scheduled);
    }
    if (station) {
      list = list
        .map((o) => ({ ...o, items: (o.items || []).filter((it) => it.station === station) }))
        .filter((o) => o.items.length);
    }
    return list;
  }, [orders, tab, station]);

  const sorted = useMemo(
    () =>
      [...visible].sort((a, b) => {
        // Scheduled lane sorts by when the guest wants it; live queues sort by
        // when the ticket fires (falls back to created_at for ASAP orders).
        const key = (o) => tab === "scheduled"
          ? parseServerDate(o.requested_at || o.created_at)
          : parseServerDate(o.fire_at || o.created_at);
        return key(a) - key(b);
      }),
    [visible, tab]
  );

  const scheduledCount = useMemo(() => orders.filter((o) => o.scheduled).length, [orders]);

  // "Ready to run": every item that's READY across all orders & stations,
  // grouped by order, so a waiter sees exactly what to pick up and from
  // which station. Ignores the station filter on purpose.
  const readyRun = useMemo(
    () =>
      orders
        .map((o) => ({ order: o, items: (o.items || []).filter((it) => it.status === "ready") }))
        .filter((g) => g.items.length > 0)
        .sort((a, b) => parseServerDate(a.order.created_at) - parseServerDate(b.order.created_at)),
    [orders]
  );
  const readyCount = useMemo(() => readyRun.reduce((n, g) => n + g.items.length, 0), [readyRun]);

  return (
    <>
      <div className="space-between" style={{ marginBottom: 16 }}>
        <h1>{t("nav_order_queue")}</h1>
        <div className="muted">
          {t("signed_in_as")} <b style={{ textTransform: "capitalize" }}>{role}</b> ·{" "}
          <span className={live ? "live-dot live" : "live-dot offline"}>
            {live ? "● " + t("live") : "● " + t("polling")}
          </span>
        </div>
      </div>

      {role === "waiter" && (
        <div className="my-tables-strip" role="region" aria-label="Your tables today">
          <span className="my-tables-label">👤 {t("my_tables_today")}</span>
          {myTables.length === 0 ? (
            <span className="my-tables-empty">
              {t("no_tables_assigned")} — {t("ask_manager")}
            </span>
          ) : (
            <div className="my-tables-chips">
              {myTables.map((a) => (
                <span key={a.table_id} className="my-table-chip">
                  {t("table")} {a.table_number}
                  {a.is_override && <span className="my-table-override" title="Today's override">★</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {stationsOn && tab !== "ready_run" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {[{ k: null, l: "All stations" }, { k: "kitchen", l: "🍳 Kitchen" }, { k: "bar", l: "🍺 Bar" }, { k: "cafe", l: "☕ Café" }].map((o) => (
            <button
              key={o.k || "all"}
              className={"btn small " + (station === o.k ? "" : "secondary")}
              onClick={() => setStation(o.k)}
            >
              {o.l}
            </button>
          ))}
        </div>
      )}

      <div className="tabs">
        {STATUS_TAB_KEYS.map((key) => (
          <div
            key={key}
            className={"tab " + (tab === key ? "active" : "")}
            onClick={() => setTab(key)}
          >
            {tabLabel(key)}
            {key === "active" && ` (${orders.length})`}
            {key === "scheduled" && scheduledCount > 0 && ` (${scheduledCount})`}
            {key === "ready_run" && readyCount > 0 && ` (${readyCount})`}
          </div>
        ))}
        <button className="btn secondary small" style={{ marginLeft: "auto" }} onClick={load}>
          🔄 {t("refresh")}
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {loading ? (
        <p className="muted">{t("loading")}</p>
      ) : tab === "ready_run" ? (
        readyRun.length === 0 ? (
          <p className="muted">{t("no_ready_items")}</p>
        ) : (
          <div className="grid-cards">
            {readyRun.map(({ order, items }) => (
              <div className="order-card status-ready" key={order.id}>
                <div className="space-between">
                  <h3 style={{ margin: 0 }}>
                    {t("table")} {order.table_number ?? "—"}
                    {order.round_number ? (
                      <span style={{ color: "#008080" }}> · {t("round")} {order.round_number}</span>
                    ) : (
                      <> · #{order.id}</>
                    )}
                  </h3>
                  {order.waiter_name && (
                    <span className="muted" style={{ fontSize: "0.75rem" }}>👤 {order.waiter_name}</span>
                  )}
                </div>
                <ul>
                  {items.map((it) => (
                    <li key={it.id} className="space-between" style={{ alignItems: "center", gap: 8 }}>
                      <div>
                        <b>{it.quantity}×</b> {it.name || `#${it.menu_id}`}{" "}
                        <span style={{ fontSize: "0.72rem", background: "#eef7f6", color: "#045", padding: "1px 8px", borderRadius: 10, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {stationLabel(it.station)}
                        </span>
                      </div>
                      <button className="btn ok small" onClick={() => handleItemStatus(order.id, it.id, "served")}>
                        {t("btn_mark_served")}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )
      ) : sorted.length === 0 ? (
        <p className="muted">{t("no_orders_here")}</p>
      ) : (
        <div className="grid-cards">
          {sorted.map((o) => (
            o.scheduled ? (
              <div key={o.id} style={{ border: "1px dashed #b8860b", borderRadius: 10, padding: 6, background: "#fffdf5" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "2px 6px 6px" }}>
                  <span style={{ fontWeight: 700, color: "#8a6d00" }}>⏰ {t("serve_at")} {serveAtLabel(o)}</span>
                  <button className="btn" onClick={() => fireNow(o.id)} style={{ padding: "3px 10px" }}>{t("fire_now")}</button>
                </div>
                <OrderCard order={o} role={role} onStatusChange={handleStatus} onItemStatusChange={station ? handleItemStatus : undefined} />
              </div>
            ) : (
              <OrderCard
                key={o.id}
                order={o}
                role={role}
                onStatusChange={handleStatus}
                onItemStatusChange={station ? handleItemStatus : undefined}
              />
            )
          ))}
        </div>
      )}
    </>
  );
}
